import { Button, InputText } from "@sellar-io/components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { usePriceTierList } from "../../../hooks/api/v2/priceTier/usePriceTierList";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { Alert } from "../../../components/alert/Alert";
import { usePriceTierCreateDerived } from "../../../hooks/api/v2/priceTier/usePriceTierCreateDerived";

export const DerivedPriceListCreateDialog = ({
  active,
  setActive,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
}) => {
  return (
    <Dialog active={active} onDismiss={() => setActive(false)}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={() => setActive(false)}
        title="New derived price list"
      />
      {active && <Content setActive={setActive} />}
    </Dialog>
  );
};

const Content = ({ setActive }: { setActive: (active: boolean) => void }) => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [selectedBaseListIdOverride, setSelectedBaseListIdOverride] = useState<
    number | undefined
  >();

  const { data: basePriceLists } = usePriceTierList({
    query: [
      {
        type: "BasePriceTierId",
        modifier: "IS_NULL",
      },
      {
        type: "derivationTypeIncludes",
        value: ["base"],
        modifier: "IN",
      },
    ],
  });

  const flatBaseLists = useMemo(
    () => basePriceLists?.pages.flat() ?? [],
    [basePriceLists],
  );

  const selectedBaseListId = selectedBaseListIdOverride ?? flatBaseLists[0]?.id;

  const createDerivedPriceList = usePriceTierCreateDerived();

  const onSubmit = () => {
    setError("");
    if (!selectedBaseListId) {
      setError("Please select a base price list");
      return;
    }
    createDerivedPriceList.mutate(
      {
        name,
        derivationType: "custom",
        derivationValue: null,
        BasePriceTierId: selectedBaseListId,
      },
      {
        onSuccess: (response) => {
          setActive(false);
          navigate(ROUTES.PRICE_LIST.href({ priceListId: response.id }));
        },
      },
    );
  };

  return (
    <>
      <DialogContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <InputLabel>Name</InputLabel>
          <InputContainer>
            <InputText
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </InputContainer>
        </div>
        {error && <Alert type="error" message={error} />}
        <div className="flex flex-col gap-2">
          <InputLabel>Select base price list</InputLabel>
          {flatBaseLists.length > 0 ? (
            <RadioGroup
              value={selectedBaseListId?.toString()}
              onChange={(value) =>
                setSelectedBaseListIdOverride(parseInt(value))
              }
            >
              <RadioGroupContainer>
                {flatBaseLists.map((pl) => (
                  <RadioGroupOption key={pl.id} value={pl.id.toString()}>
                    {pl.name}
                  </RadioGroupOption>
                ))}
              </RadioGroupContainer>
            </RadioGroup>
          ) : (
            <Alert
              type="information"
              title="Cannot create a derived price list before creating a base price list"
              message="Go back and create a base list first which you can then use to derive other lists from."
            />
          )}
        </div>
      </DialogContent>
      <DialogCTA>
        <Button
          disabled={!name || !selectedBaseListId || flatBaseLists.length === 0}
          loading={createDerivedPriceList.isPending}
          onClick={onSubmit}
        >
          Create
        </Button>
      </DialogCTA>
    </>
  );
};
